/**
 * Room around a text's canvas for the ink an italic glyph draws outside its advance box.
 *
 * Phaser sizes a Text's canvas to the advance width and the font's ascent and descent.
 * Italic glyphs lean past that box: a bold italic "f" hangs out on both sides, the tail
 * of a "p" on the left. Without padding the canvas cuts them off, which is how dynamics
 * (p, f, mf, sfz) and italic words (dolce, cresc.) came out cropped. Upright text is
 * left as it is.
 */

export type TextPadding = { left: number; right: number; top: number; bottom: number };

const NO_PADDING: TextPadding = { left: 0, right: 0, top: 0, bottom: 0 };

/** Horizontal room, as a share of the font size. A bold italic "f" needs about 0.16. */
const ITALIC_SIDE_ROOM = 0.25;

/** Vertical room, as a share of the font size, for swashes past the measured ascent / descent. */
const ITALIC_END_ROOM = 0.1;

const POINTS_TO_PIXELS = 4 / 3;

/** A CSS font size ("23px", "8pt", "12") in pixels; NaN when it cannot be read. */
export const fontSizeInPixels = (fontSize: string): number => {
  if (typeof fontSize !== 'string') return Number.NaN;
  const match = /^\s*([\d.]+)\s*(px|pt)?\s*$/i.exec(fontSize);
  if (!match) return Number.NaN;
  const size = Number(match[1]);
  return match[2]?.toLowerCase() === 'pt' ? size * POINTS_TO_PIXELS : size;
};

/** The padding a text needs so none of its glyphs is cut off; none for upright text.
 *  `fontStyle` is a CSS style such as 'italic' or 'bold italic'. */
export const overhangPaddingFor = (fontStyle: string, fontSize: string): TextPadding => {
  if (typeof fontStyle !== 'string' || !/\bitalic\b/i.test(fontStyle)) return NO_PADDING;
  const sizeInPixels = fontSizeInPixels(fontSize);
  if (!Number.isFinite(sizeInPixels) || sizeInPixels <= 0) return NO_PADDING;
  const side = Math.ceil(sizeInPixels * ITALIC_SIDE_ROOM);
  const end = Math.ceil(sizeInPixels * ITALIC_END_ROOM);
  return { left: side, right: side, top: end, bottom: end };
};
